import { API } from "@swivl-mono/api-helper";
import { warn } from "@swivl-mono/helpers";
import {
  GoogleLocation,
  ReviewGetType,
  ReviewInterface,
  ReviewPlatform,
  ReviewReplyPlatform,
  SequenceConditions,
  SequenceInterface,
  SequenceRecordInterface,
  SequenceSubType,
  SequenceType,
  SequenceUserEventInterface,
} from "@swivl-mono/types";
import { create } from "zustand";

import { Member } from "../Member/Member.Current";

interface StoreInterface {
  sequences?: { [sequenceId: string]: SequenceInterface };
  sequenceUserEvents?: { [sequenceId: string]: SequenceUserEventInterface[] };
  sequenceRecords?: { [sequenceId: string]: SequenceRecordInterface[] };
}
const BlankState: StoreInterface = {
  sequences: undefined,
  sequenceUserEvents: undefined,
  sequenceRecords: undefined,
};

export default class SequencesModel {
  static useState = create<StoreInterface>((set) => ({ ...BlankState }));
  static setState = SequencesModel.useState.setState;
  static get state() {
    return SequencesModel.useState.getState();
  }
  static reset() {
    SequencesModel.setState({ ...BlankState });
  }

  static async load(force: boolean) {
    if (!force && SequencesModel.state.sequences) {
      return;
    }
    API.Sequences.getSequences(Member.orgId)
      .then((sequences) => {
        let sequenceMap = {};
        sequences.forEach((sequence) => {
          sequenceMap[sequence.id] = sequence;
        });
        SequencesModel.setState({
          sequences: sequenceMap,
        });
      })
      .catch((e) => {
        warn("Error Loading Sequences state");
      });
  }

  static loadSequenceRecordsForSequence(sequenceId: number) {
    API.Sequences.getSequenceRecords(Member.orgId, sequenceId)
      .then((records) => {
        SequencesModel.setState({
          sequenceRecords: {
            ...SequencesModel.state.sequenceRecords,
            [sequenceId]: records,
          },
        });
      })
      .catch((e) => {
        warn("Error Loading Sequence Records");
      });
  }
  static loadSequenceUserEventsForSequence(sequenceId: number, recordId: number) {
    API.Sequences.getSequenceUserEventsForRecord(Member.orgId, sequenceId, recordId)
      .then((events) => {
        SequencesModel.setState({
          sequenceUserEvents: {
            ...SequencesModel.state.sequenceUserEvents,
            [sequenceId]: events,
          },
        });
      })
      .catch((e) => {
        warn("Error Loading Sequence Events");
      });
  }

  static createSequence(name: string, type: SequenceType, subType: SequenceSubType, questSlug: string) {
    return API.Sequences.createSequence(Member.orgId, name, type, subType, questSlug)
      .then((sequence) => {
        SequencesModel.setState({
          sequences: {
            ...SequencesModel.state.sequences,
            [sequence.id]: sequence,
          },
        });
        return sequence;
      })
      .catch((e) => {
        warn("Error Creating Sequence");
      });
  }

  static updateSequence(sequenceId: number, sequence: Partial<SequenceInterface>, shouldSave: boolean) {
    let oldSequence = SequencesModel.state.sequences?.[sequenceId];
    if (!oldSequence) {
      warn("Sequence not found");
      return;
    }
    const updatedSequence = { ...oldSequence, ...sequence };
    SequencesModel.setState({
      sequences: {
        ...SequencesModel.state.sequences,
        [sequenceId]: updatedSequence,
      },
    });

    if (shouldSave) {
      API.Sequences.updateSequence(Member.orgId, sequenceId, sequence)
        .then((updated) => {
          SequencesModel.setState({
            sequences: {
              ...SequencesModel.state.sequences,
              [sequenceId]: updated,
            },
          });
        })
        .catch((e) => {
          warn("Error Saving Sequence");
        });
    }
  }
}
